import { useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CreateClassInProgramForm from "../Forms/CreateClassInProgramForm";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AddNewClass,
  AddTeachersToClass,
  HandleAddNewTeacher,
} from "../../actions/teacherMutations";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const createClassWithTeachers = async ({
  classToAdd,
  existingTeacherIds,
  newTeachers,
}) => {
  const createdClassId = (await AddNewClass({ cl: classToAdd })).data.result.id;

  const teacherResults = await Promise.allSettled(
    newTeachers.map((teacher) => HandleAddNewTeacher(teacher, createdClassId)),
  );
  const teacherErrors = teacherResults
    .filter((result) => result.status === "rejected")
    .map((result) => result.reason);

  if (existingTeacherIds.length > 0) {
    try {
      await AddTeachersToClass({
        teacherIds: existingTeacherIds,
        classId: createdClassId,
      });
    } catch (err) {
      teacherErrors.push(err);
    }
  }

  return { createdClassId, teacherErrors };
};

export default function CreateClassInProgram(props) {
  const [newTeachersToAdd, setNewTeachersToAdd] = useState([]);
  const queryClient = useQueryClient();
  const intl = useIntl();

  const toggleModal = () => {
    setNewTeachersToAdd([]);
    props.toggle();
  };

  const mutateCreateClass = useMutation({
    mutationFn: createClassWithTeachers,
    onSuccess: ({ createdClassId, teacherErrors }) => {
      props.onClassCreated?.(createdClassId);
      toggleModal();
      queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
      queryClient.invalidateQueries({ queryKey: ["FetchProgramById"] });
      queryClient.invalidateQueries({
        queryKey: ["FetchSchoolDataBySchoolId"],
      });
      toast.success(<FormattedMessage id="ClassUpdated" />);
      teacherErrors.forEach((err) =>
        toast.warn(formatServiceError(intl, err?.response?.data)),
      );
    },
    onError: (err) => {
      toggleModal();
      toast.warn(formatServiceError(intl, err?.response?.data));
    },
  });

  const handleAddedClass = (cl) => {
    mutateCreateClass.mutate({
      classToAdd: {
        name: cl.name,
        program: props.program,
        orientation: cl.orientation,
      },
      existingTeacherIds: cl.teachers,
      newTeachers: newTeachersToAdd,
    });
  };

  return (
    <Modal
      style={{ maxWidth: "700px", width: "80%" }}
      isOpen={props.isOpen}
      toggle={toggleModal}
    >
      <ModalHeader>
        <FormattedMessage
          id="CreateClass"
          values={{ programName: props.program?.name }}
        />
      </ModalHeader>
      <ModalBody>
        <CreateClassInProgramForm
          teachers={props.teachers}
          toggleModal={toggleModal}
          handleAddedClass={handleAddedClass}
          newTeachersToAdd={newTeachersToAdd}
          setNewTeachersToAdd={setNewTeachersToAdd}
        />
      </ModalBody>
    </Modal>
  );
}
